import React, { useState, useEffect } from "react";
import "./style.css";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Form, Input, Button, message, Select, DatePicker, Radio, Divider } from "antd";
import { 
    UserOutlined, 
    MailOutlined, 
    LockOutlined, 
    GlobalOutlined, 
    CalendarOutlined,
    EnvironmentOutlined 
} from "@ant-design/icons";
import { Country, State, City } from "country-state-city";
import { NavLink } from "react-router-dom";
import LinkedInOAuth from "./loginComponents/LinkdingLoginnew";
import CustomButton from "./loginComponents/customlogin";
import AppleLogin from "./loginComponents/AppleLogin";
import TwitterOauthButton from "./loginComponents/Twitter";
import { register, socialLogin } from "../redux/slice/authSlice";

const { TextArea } = Input;

const Register = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedState, setSelectedState] = useState(null);
    const [isChecked, setIsChecked] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { status, error } = useSelector((state) => state.auth);
    const referrerCode = sessionStorage.getItem("referrerCode");

    useEffect(() => {
        const allCountries = Country.getAllCountries();
        setCountries(allCountries);
        console.log("allCountries", allCountries);
    }, []);

    const handleCountryChange = (countryCode) => {
        setSelectedCountry(countryCode);
        const countryStates = State.getStatesOfCountry(countryCode);
        setStates(countryStates);
        setCities([]);
        setSelectedState(null);
        form.setFieldsValue({ state: undefined, city: undefined });
    };

    const handleStateChange = (stateCode) => {
        setSelectedState(stateCode);
        const stateCities = City.getCitiesOfState(selectedCountry, stateCode);
        setCities(stateCities);
        form.setFieldsValue({ city: undefined });
    };

    const handleCheckboxChange = (e) => {
        e.stopPropagation();
        setIsChecked(!isChecked);
        setErrorMessage("");
    };

    const handleSocialClick = () => {
        if (!isChecked) {
            setErrorMessage("Please agree to the terms by checking the checkbox.");
        } else {
            setErrorMessage("");
        }
    };

    const onFinish = async (values) => {
        if (!isChecked) {
            setErrorMessage("Please agree to the terms by checking the checkbox.");
            return;
        }

        if (values.password !== values.confirmPassword) {
            message.error("Passwords do not match");
            return;
        }

        setLoading(true);
        try {
            // Get country, state, city names for submission
            const countryObj = countries.find(c => c.isoCode === values.country);
            const stateObj = states.find(s => s.isoCode === values.state);
            const cityObj = cities.find(c => c.name === values.city);

            const registerPayload = {
                userName: values.fullName,
                email: values.email,
                password: values.password,
                confirmPassword: values.confirmPassword,
                userType: "respondent",
                isActive: true,
                gender: values.gender || "",
                dob: values.dob ? values.dob.format('YYYY-MM-DD') : null,
                mobile: values.mobile || "",
                referrerSub: referrerCode || "",
                signupProvider: "LOCAL",
                balance: 0,
                currency: countryObj?.currency || "INR",
                latitude: parseFloat(countryObj?.latitude) || 0,
                longitude: parseFloat(countryObj?.longitude) || 0,
                city: cityObj?.name || values.city,
                state: stateObj?.name || values.state,
                country: countryObj?.name || values.country,
                countryCode: countryObj?.isoCode || values.country,
                phoneCode: countryObj?.phonecode || "",
                postal: values.postal || ""
            };
            
            const result = await dispatch(register(registerPayload));
            
            if (register.fulfilled.match(result)) {
                message.success("Registration successful! Redirecting to dashboard...");
                setTimeout(() => {
                    navigate("/dashboard");
                }, 1000);
            } else if (register.rejected.match(result)) {
                message.error(result.payload || "Registration failed. Please try again.");
            }
        } catch (error) {
            message.error("Registration failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="register-section py-5">
            <div className="container py-5">
                <div className="row justify-content-center g-0 py-5">
                    {/* Left side - Register Form */}
                    <div className="col-lg-6 col-md-12">
                        <div className="register-form-wrapper">
                            <div className="register-form-content">
                                <h1 className="register-title">
                                    Join Poll Sphere and start earning rewards
                                </h1>
                                <p className="register-subtitle">
                                    Create your account to participate in surveys and earn rewards
                                </p>

                                <div className="register-form">
                                    <Form
                                        form={form}
                                        name="register"
                                        onFinish={onFinish}
                                        layout="vertical"
                                        scrollToFirstError
                                    >
                                        <div className="row">
                                            <div className="col-md-6">
                                                <Form.Item
                                                    name="fullName"
                                                    rules={[
                                                        { required: true, message: "Please enter your full name" },
                                                        { min: 2, message: "Name must be at least 2 characters" }
                                                    ]}
                                                >
                                                    <Input
                                                        prefix={<UserOutlined />}
                                                        placeholder="Full Name"
                                                        size="large"
                                                        className="register-input"
                                                    />
                                                </Form.Item>
                                            </div>
                                            <div className="col-md-6">
                                                <Form.Item
                                                    name="email"
                                                    rules={[
                                                        { required: true, message: "Please enter your email" },
                                                        { type: "email", message: "Please enter a valid email" }
                                                    ]}
                                                >
                                                    <Input
                                                        prefix={<MailOutlined />}
                                                        placeholder="Email Address"
                                                        size="large"
                                                        className="register-input"
                                                    />
                                                </Form.Item>
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-6">
                                                <Form.Item
                                                    name="dob"
                                                    rules={[{ required: true, message: "Please select your date of birth" }]}
                                                >
                                                    <DatePicker
                                                        placeholder="Date of Birth"
                                                        size="large"
                                                        className="register-input register-datepicker"
                                                        format="DD/MM/YYYY"
                                                        suffixIcon={<CalendarOutlined />}
                                                        style={{ width: '100%' }}
                                                    />
                                                </Form.Item>
                                            </div>
                                        </div>

                                        {/* Gender Field */}
                                        <Form.Item
                                            name="gender"
                                            rules={[{ required: true, message: "Please select your gender" }]}
                                        >
                                            <Radio.Group className="gender-radio-group">
                                                <Radio value="male">Male</Radio>
                                                <Radio value="female">Female</Radio>
                                                <Radio value="other">Other</Radio>
                                            </Radio.Group>
                                        </Form.Item>

                                        {/* Country, State, City Row */}
                                        <div className="row">
                                            <div className="col-md-4">
                                                <Form.Item
                                                    name="country"
                                                    rules={[{ required: true, message: "Please select country" }]}
                                                >
                                                    <Select
                                                        showSearch
                                                        placeholder="Select Country"
                                                        size="large"
                                                        className="register-select"
                                                        optionFilterProp="label"
                                                        onChange={handleCountryChange}
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        suffixIcon={<GlobalOutlined />}
                                                        options={countries.map((country) => ({
                                                            value: country.isoCode,
                                                            label: `${country.flag} ${country.name}`
                                                        }))}
                                                    />
                                                </Form.Item>
                                            </div>
                                            <div className="col-md-4">
                                                <Form.Item
                                                    name="state"
                                                    rules={[{ required: true, message: "Please select state" }]}
                                                >
                                                    <Select
                                                        showSearch
                                                        placeholder="Select State"
                                                        size="large"
                                                        className="register-select"
                                                        optionFilterProp="label"
                                                        onChange={handleStateChange}
                                                        disabled={!selectedCountry}
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        suffixIcon={<EnvironmentOutlined />}
                                                        options={states.map((state) => ({
                                                            value: state.isoCode,
                                                            label: state.name
                                                        }))}
                                                    />
                                                </Form.Item>
                                            </div>
                                            <div className="col-md-4">
                                                <Form.Item
                                                    name="city"
                                                    rules={[{ required: true, message: "Please select city" }]}
                                                >
                                                    <Select
                                                        showSearch
                                                        placeholder="Select City"
                                                        size="large"
                                                        className="register-select"
                                                        optionFilterProp="label"
                                                        disabled={!selectedState}
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        suffixIcon={<EnvironmentOutlined />}
                                                        options={cities.map((city) => ({
                                                            value: city.name,
                                                            label: city.name
                                                        }))}
                                                    />
                                                </Form.Item>
                                            </div>
                                        </div>


                                        <div className="row">
                                            <div className="col-md-6">
                                                <Form.Item
                                                    name="password"
                                                    rules={[
                                                        { required: true, message: "Please enter your password" },
                                                        { min: 8, message: "Password must be at least 8 characters" },
                                                        { pattern: /[A-Z]/, message: "Password must contain an uppercase letter" },
                                                        { pattern: /[a-z]/, message: "Password must contain a lowercase letter" },
                                                        { pattern: /\d/, message: "Password must contain a digit" },
                                                        { pattern: /[@$!%*?&]/, message: "Password must contain a special character (@$!%*?&)" }
                                                    ]}
                                                >
                                                    <Input.Password
                                                        prefix={<LockOutlined />}
                                                        placeholder="Password"
                                                        size="large"
                                                        className="register-input"
                                                    />
                                                </Form.Item>
                                            </div>
                                            <div className="col-md-6">
                                                <Form.Item
                                                    name="confirmPassword"
                                                    dependencies={['password']}
                                                    rules={[
                                                        { required: true, message: "Please confirm your password" },
                                                        ({ getFieldValue }) => ({
                                                            validator(_, value) {
                                                                if (!value || getFieldValue('password') === value) {
                                                                    return Promise.resolve();
                                                                }
                                                                return Promise.reject(new Error('Passwords do not match'));
                                                            },
                                                        }),
                                                    ]}
                                                >
                                                    <Input.Password
                                                        prefix={<LockOutlined />}
                                                        placeholder="Confirm Password"
                                                        size="large"
                                                        className="register-input"
                                                    />
                                                </Form.Item>
                                            </div>
                                        </div>

                                        {/* Terms Checkbox */}
                                        <div className="form-check d-flex align-items-start ps-0 mb-3">
                                            <input
                                                className="form-check-input ms-0 mt-1"
                                                type="checkbox"
                                                id="registerTermsCheckForm"
                                                checked={isChecked}
                                                onChange={handleCheckboxChange}
                                            />
                                            <label className="terms-label ms-3" htmlFor="registerTermsCheckForm">
                                                I agree to the <NavLink to="/term-service">Terms</NavLink> of Use and
                                                to receive marketing email messages from Poll Sphere, and I accept the{" "}
                                                <NavLink to="/privacy-policy">Privacy Policy</NavLink>.
                                            </label>
                                        </div>
                                        {errorMessage && (
                                            <div className="error-message text-danger mb-3">{errorMessage}</div>
                                        )}

                                        <Form.Item>
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                                size="large"
                                                loading={loading || status === "loading"}
                                                className="register-btn"
                                                block
                                            >
                                                Create Account
                                            </Button>
                                        </Form.Item>
                                    </Form>

                                    <Divider>OR continue with</Divider>

                                    {/* Social Login Section */}
                                    <div className="social-login-section">
                                        {!isChecked ? (
                                            <div className="social-btns-grid">
                                                <button className="login-btn" onClick={handleSocialClick}>
                                                    <img src="./images/login1.png" alt="Google" />
                                                    <span>Continue with Google</span>
                                                </button>
                                                <button className="login-btn" onClick={handleSocialClick}>
                                                    <img src="./images/login3.png" alt="Apple" />
                                                    <span>Continue with Apple</span>
                                                </button>
                                                <button className="login-btn" onClick={handleSocialClick}>
                                                    <img src="./images/login2.png" alt="LinkedIn" />
                                                    <span>Continue with LinkedIn</span>
                                                </button>
                                                <button className="login-btn" onClick={handleSocialClick}>
                                                    <img src="./images/twitter.png" alt="Twitter" />
                                                    <span>Continue with Twitter</span>
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="social-btns-grid">
                                                <CustomButton disabled={!isChecked} onClick={handleSocialClick} />
                                                <AppleLogin disabled={!isChecked} onClick={handleSocialClick} />
                                                <LinkedInOAuth disabled={!isChecked} onClick={handleSocialClick} />
                                                <TwitterOauthButton disabled={!isChecked} onClick={handleSocialClick} />
                                            </div>
                                        )}
                                    </div>

                                    <div className="auth-switch-link mt-4">
                                        <p>
                                            Already have an account?{" "}
                                            <NavLink to="/">Login here</NavLink>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                  
                </div>
            </div>
        </section>
    );
};

export default Register;
